import React, { useCallback, useContext, useState } from "react";

import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../context/domain/planning/DispatchContext";

import { EditScopeCard } from "./workScopeEdit/EditScopeCard";
import WorkScopeModal from "./WorkScopeModal";
import { ConfirmModal } from "../../../components/ui";

import { getDateString } from "../../../utils/datetime";
import { getUserDisplayName } from "../../../utils/business";

import { addWorkScopeAspect } from "../../../services/api/dispatch";

import { FiCopy } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";

/**
 * Component for the work scope box in the Daily Dispatch app.
 *
 * @NOTE This is really badly coded and inefficient with strange logic and state
 * management, prop drilling, and hardcoded data. It needs to be refactored.
 *
 * Coded by Ben.
 *
 * @comment Aaron: Somehow this works, so I am not refactoring it for now as we are
 * massively behind schedule. But it is really bad code and needs simplifying, I am sure
 * it will break at some point.
 *
 * @Note2 This would be a huge priority if time to refactor is found but may take a few
 * days to do so.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.dayScopes - Scope rows for the day.
 * @param {Object} props.job - Job row for this dispatch column.
 * @param {Date} props.date - Calendar date for the scopes.
 * @returns {JSX.Element}
 */
export default function EditWorkScopes({ dayScopes, job, date }) {
  const { scopes, reloadScopes } = useContext(WeeklyContext);
  const { copyScopeOrigin, pasteScopes, isReadOnly, runPlanningOperation } =
    useContext(DispatchContext);
  const [conflictingWorkers, setConflictingWorkers] = useState([]);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);
  const [isAddScopeModalOpen, setIsAddScopeModalOpen] = useState(false);
  const [isConflictingWorkersModalOpen, setIsConflictingWorkersModalOpen] =
    useState(false);

  // Add scope to the job
  /**
   * Persist a newly chosen work scope on this job/date, then reload weekly scopes.
   *
   * @param {Object} scopeObject Catalog work-scope row.
   * @param {number|string} quantity Quantity entered in the add modal.
   * @returns {Promise<void>}
   */
  const handleAddWorkScopeAspect = useCallback(
    async (scopeObject, quantity) => {
      if (scopeObject) {
        const newScope = {
          scopeObject,
          quantity,
          date: getDateString(date),
          job: job.id,
        };
        await runPlanningOperation({
          module: "dispatch",
          operation: "addWorkScopeAspect",
          resources: ["dispatch.aspects"],
          apiCall: () => addWorkScopeAspect(newScope),
        });
        reloadScopes();
      }
      setIsAddScopeModalOpen(false);
    },
    [date, job, reloadScopes, runPlanningOperation]
  );

  // Paste scopes from the clipboard (copyScopeOrigin)
  /**
   * Paste the copied scope(s) onto this job/date after any conflict modal is dismissed.
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const handlePasteScopes = useCallback(async () => {
    setIsConflictingWorkersModalOpen(false);
    await pasteScopes("job", job.id, getDateString(date));
  }, [pasteScopes, date, job.id]);

  /**
   * Warn when pasted workers are already assigned on the destination day, else paste.
   *
   * @param {void}
   * @returns {void}
   */
  const checkPasteConflicts = useCallback(() => {
    // [0] A single copy action array will always only have one element
    const origin = scopes.find((scope) => scope.id === copyScopeOrigin[0]);
    const originWorkers =
      origin && !origin.skipped
        ? origin.worker_assignment.map((worker) => worker.id)
        : [];
    const destinationWorkers = scopes
      .filter((scope) => scope.date === getDateString(date) && !scope.skipped)
      .flatMap((scope) => scope.worker_assignment.map((worker) => worker.id));
    const conflictingWorkerIds = destinationWorkers.filter(
      (worker) => originWorkers.includes(worker) // To warn user of conflicting workers
    );
    setConflictingWorkers(
      // State is read by modal
      conflictingWorkerIds.map((workerId) =>
        origin?.worker_assignment.find((worker) => worker.id === workerId)
      )
    );
    if (conflictingWorkerIds.length > 0) {
      setIsConflictingWorkersModalOpen(true);
    } else {
      handlePasteScopes();
    }
  }, [scopes, copyScopeOrigin, date, handlePasteScopes]);

  return (
    <>
      {job && date && dayScopes && (
        <div className={"flex flex-col"}>
          {dayScopes.map((scope, index) => (
            <EditScopeCard
              index={index}
              date={date}
              key={scope.id}
              scope={scope}
              nullScope={scope.id === null}
              isConfirmDeleteModalOpen={isConfirmDeleteModalOpen}
              setIsConfirmDeleteModalOpen={setIsConfirmDeleteModalOpen}
              className="transition-all duration-500 ease-in-out transform"
            />
          ))}
          {copyScopeOrigin && copyScopeOrigin.length > 0 ? (
            <button
              className={
                "text-gray-500 font-medium rounded outline-1 outline-dashed outline-gray-400 bg-blue-100 animate-pulse hover:bg-gray-300"
              }
              onClick={() => checkPasteConflicts()}
            >
              <div className="flex flex-row justify-center items-center">
                <FiCopy className="mr-2" />
                <div>Paste scope</div>
              </div>
            </button>
          ) : (
            <>
              {!isReadOnly && (
                <button
                  onClick={() => setIsAddScopeModalOpen(true)}
                  className="p-0"
                >
                  <div className="flex flex-row hover:bg-blue-100 ease-in-out justify-center h-6 items-center bg-blue-50 rounded outline-dashed outline-1 outline-blue-300 text-md">
                    <FaPlus className="text-sm" />
                    <div className="ml-2">Add Scope</div>
                  </div>
                </button>
              )}
            </>
          )}
          <WorkScopeModal
            mode="add"
            isOpen={isAddScopeModalOpen}
            onClose={() => setIsAddScopeModalOpen(false)}
            onConfirm={handleAddWorkScopeAspect}
            job={job}
            date={date}
          />
          <ConfirmModal
            isOpen={isConflictingWorkersModalOpen}
            title={"Workers Conflict"}
            list={conflictingWorkers.map((worker) => getUserDisplayName(worker))}
            text={
              "Clicking confirm will paste the scope and remove the conflicting workers from the scope."
            }
            buttonText={"Confirm"}
            onConfirm={() => handlePasteScopes()}
            onClose={() => [
              setIsConflictingWorkersModalOpen(false),
              setConflictingWorkers([]),
              // setCopyScopeOrigin([]),
            ]}
          />
        </div>
      )}
    </>
  );
}
